/**
 * Type Badge Component
 *
 * A badge-styled dropdown for selecting field data types.
 * Shows icon + label, looks like a badge but functions as a dropdown.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@loader/ui';
import {
  FIELD_DATA_TYPES,
  FIELD_DATA_TYPE_LABELS,
  type FieldDataType,
} from '@loader/shared-types';

import { getTypeIcon } from '../constants/fieldTypes';

interface TypeBadgeProps {
  /** Current data type */
  dataType: FieldDataType;
  /** Called when type changes */
  onTypeChange: (type: FieldDataType) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether this field has a pending change */
  isPending?: boolean;
}

export function TypeBadge({
  dataType,
  onTypeChange,
  disabled,
  isPending,
}: TypeBadgeProps) {
  const CurrentIcon = getTypeIcon(dataType);
  const currentLabel =
    FIELD_DATA_TYPE_LABELS[dataType as FieldDataType] || dataType;

  return (
    <Select
      value={dataType}
      onValueChange={(value) => onTypeChange(value as FieldDataType)}
      disabled={disabled}
    >
      <SelectTrigger
        className={cn(
          'h-7 w-[110px] gap-1.5 rounded border-none px-2 py-1',
          'bg-muted/50 hover:bg-muted focus:ring-1 focus:ring-primary/30',
          'text-sm font-medium shadow-none',
          isPending && 'ring-2 ring-primary/30',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <SelectValue>
          <div className="flex items-center gap-1.5">
            <CurrentIcon className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{currentLabel}</span>
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="start">
        {FIELD_DATA_TYPES.map((type) => {
          const Icon = getTypeIcon(type);
          const label = FIELD_DATA_TYPE_LABELS[type];

          return (
            <SelectItem key={type} value={type}>
              <Icon className="h-3.5 w-3.5" />
              <span>{label}</span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
